import React from "react";
import FlowPanel from "./FlowPanel";

const NodeEditorCanvas = React.memo(({
  flowAreaRef,
  panOffset,
  zoom,
  safeGridWidth,
  safeGridHeight,
  minX,
  minY,
  gridDots,
  connections,
  flowItems,
  animatingPanels,
  dragOverPosition,
  draggedPanel,
  hoveredStackingItem,
  stackingOrder,
  expandedComponents,
  dragOverStep,
  isPanning,
  isScrollingRef,
  isScrolling,
  timelineHeight,
  currentAction,
  ACTION_STATE,
  hoveredTimelineComponentId,
  onMouseDown,
  onFlowAreaDragOver,
  onFlowAreaDragLeave,
  onInsertionDrop,
  onPanelDragStart,
  onPanelDragEnd,
  onStepNameChange,
  onComponentNameChange,
  isEditingName,
  setIsEditingName,
  onTimelineResizeStart,
  onComponentDragStart,
  onComponentDragEnd,
  onStepContextMenu,
  onComponentContextMenu,
  onComponentClick,
  onComponentHover,
  onBackgroundClick,
  leftListNewCustomisationItems,
  campaignData,
  customisationVars,
  selectedFeatureId,
  selectedFeatureNode
}) => {
  return (
    <>
      <div 
        className={`flow-area ${isPanning ? 'panning' : ''} ${currentAction === ACTION_STATE?.PANNING ? 'panning-active' : ''}`}
        ref={flowAreaRef}
        style={{ flex: 1, position: 'relative' }}
        onClick={(e) => {
          if (!onBackgroundClick) return;
          const clickedOnComponent = e.target.closest?.('.component-item');
          const clickedOnPanel = e.target.closest?.('.flow-panel');
          if (!clickedOnComponent && !clickedOnPanel) onBackgroundClick();
        }}
      >
        <div 
          className="node-editor-section"
          onMouseDown={onMouseDown}
          onDragOver={onFlowAreaDragOver}
          onDragLeave={onFlowAreaDragLeave}
          onDrop={onInsertionDrop}
        >
        <div 
          className="flow-container"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            width: safeGridWidth,
            height: safeGridHeight,
          }}
        >
          {/* <div 
            className="grid-bg"
            style={{
              width: safeGridWidth,
              height: safeGridHeight,
            }}
          >
            {/* Render grid dots */}
            {/*gridDots.map((dot) => (
              <div
                key={dot.key}
                className="grid-dot"
                style={{
                  left: dot.x,
                  top: dot.y,
                }}
              />
            ))}
          </div> */}
          
          {/* Render connection lines */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: safeGridWidth,
              height: safeGridHeight,
              pointerEvents: 'none',
              zIndex: 2,
            }}
            viewBox={`0 0 ${safeGridWidth} ${safeGridHeight}`}
          >
            {connections.map((connection) => (
              <g key={connection.key}>
                {/* Only render line if it's not just a dot */}
                {!connection.isJustDot && (
                  <line
                    x1={connection.x1}
                    y1={connection.y1}
                    x2={connection.x2}
                    y2={connection.y2}
                    stroke={connection.isExpansionLine ? connection.strokeColor : "#007bff"}
                    strokeWidth={connection.isExpansionLine ? "1" : "2"}
                    strokeDasharray={connection.isExpansionLine ? "4,3" : "4,4"}
                    opacity={connection.isExpansionLine ? "0.75" : "1"}
                  />
                )}
                {/* Render dots using x,y properties (not x1,y1) */}
                {connection.isJustDot && connection.x !== undefined && connection.y !== undefined && (
                  <circle
                    cx={connection.x}
                    cy={connection.y}
                    r="4"
                    fill="#007bff"
                  />
                )}
              </g>
            ))}
          </svg>
          
          {/* Render flow items above the grid */}
          {flowItems.map((item) => {
            // Calculate dynamic positioning for insertion animation
            // Offset positions by minX/minY so all coordinates start from 0
            let dynamicStyle = {
              left: item.x - minX,
              top: item.y - minY,
              width: item.width,
              height: item.height,
              transition: animatingPanels.includes(item.id) ? 'left 300ms ease-out' : 'none',
            };

            // Keep panel positions stable during insertion preview so connector lines remain visually aligned.

            // Add visual feedback for the panel being dragged
            if (draggedPanel && item.id === draggedPanel.id) {
              dynamicStyle.opacity = 0.5;
              dynamicStyle.transform = 'scale(0.95)';
              dynamicStyle.transition = 'none';
            }

            // Skip plus signs
            if (item.type === 'plus') {
              return null;
            }

            return (
              <FlowPanel
                key={item.id}
                item={item}
                dynamicStyle={dynamicStyle}
                hoveredStackingItem={hoveredStackingItem}
                hoveredTimelineComponentId={hoveredTimelineComponentId}
                stackingOrder={stackingOrder}
                expandedComponents={expandedComponents}
                dragOverStep={dragOverStep}
                isScrollingRef={isScrollingRef}
                isScrolling={isScrolling}
                isPanning={isPanning}
                onPanelDragStart={onPanelDragStart}
                onPanelDragEnd={onPanelDragEnd}
                onStepNameChange={onStepNameChange}
                onComponentNameChange={onComponentNameChange}
                isEditingName={isEditingName}
                setIsEditingName={setIsEditingName}
                onComponentDragStart={onComponentDragStart}
                onComponentDragEnd={onComponentDragEnd}
                onStepContextMenu={onStepContextMenu}
                onComponentContextMenu={onComponentContextMenu}
                onInsertionDrop={onInsertionDrop}
                onFlowAreaDragOver={onFlowAreaDragOver}
                onComponentClick={onComponentClick}
                onComponentHover={onComponentHover}
                selectedFeatureId={selectedFeatureId}
                selectedFeatureNode={selectedFeatureNode}
                campaignData={campaignData}
                customisationVars={customisationVars}
                leftListNewCustomisationItems={leftListNewCustomisationItems}
                panOffset={panOffset}
              />
            );
          })}
          
          {/* Render temporary insertion plus sign when dragging between panels */}
          {dragOverPosition && (
            <div
              className="plus-sign insertion-preview"
              style={{
                left: dragOverPosition.x - minX,
                top: dragOverPosition.y - minY,
              }}
            >
              +
            </div>
          )}
        </div>
        </div>
      </div>
      
      {/* Resize handle */}
      {/* <div 
        className="timeline-resize-handle"
        onMouseDown={onTimelineResizeStart}
      >
        <div className="resize-handle-line"></div>
      </div> */}
    </>
  );
}, (prevProps, nextProps) => {
  // Only re-render if critical props change
  // hoveredStackingItem changes frequently but doesn't need full re-render
  return (
    prevProps.panOffset.x === nextProps.panOffset.x &&
    prevProps.panOffset.y === nextProps.panOffset.y &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.isPanning === nextProps.isPanning &&
    prevProps.isEditingName === nextProps.isEditingName &&
    prevProps.draggedPanel?.id === nextProps.draggedPanel?.id &&
    prevProps.dragOverPosition === nextProps.dragOverPosition &&
    prevProps.hoveredStackingItem === nextProps.hoveredStackingItem &&
    prevProps.hoveredTimelineComponentId === nextProps.hoveredTimelineComponentId &&
    prevProps.selectedFeatureId === nextProps.selectedFeatureId &&
    prevProps.onBackgroundClick === nextProps.onBackgroundClick &&
    prevProps.flowItems.length === nextProps.flowItems.length &&
    JSON.stringify(prevProps.connections.map(conn => ({
      key: conn.key,
      x1: conn.x1,
      y1: conn.y1,
      x2: conn.x2,
      y2: conn.y2,
      x: conn.x,
      y: conn.y,
      isExpansionLine: conn.isExpansionLine,
      strokeColor: conn.strokeColor,
      isJustDot: conn.isJustDot
    }))) === JSON.stringify(nextProps.connections.map(conn => ({
      key: conn.key,
      x1: conn.x1,
      y1: conn.y1,
      x2: conn.x2,
      y2: conn.y2,
      x: conn.x,
      y: conn.y,
      isExpansionLine: conn.isExpansionLine,
      strokeColor: conn.strokeColor,
      isJustDot: conn.isJustDot
    }))) &&
    JSON.stringify(prevProps.flowItems.map(fi => ({ id: fi.id, x: fi.x, y: fi.y, label: fi.label, componentsCount: fi.components?.length || 0 }))) === 
    JSON.stringify(nextProps.flowItems.map(fi => ({ id: fi.id, x: fi.x, y: fi.y, label: fi.label, componentsCount: fi.components?.length || 0 })))
  );
});

export default NodeEditorCanvas;
